import React, { ReactNode, useRef } from 'react';
import { DOMAttributes } from '@react-types/shared';
import clsx from 'clsx';
import { FocusRing } from 'react-aria/FocusRing';
import { useLink, AriaLinkOptions } from 'react-aria/useLink';
import { getWrappedElement } from '@react-spectrum/utils';
import styles from '@danske/sapphire-css/components/link/link.module.css';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';
import { useThemeCheck } from '../../utils/useThemeCheck';
import {
  AriaLabelingPropsKeys,
  FocusablePropsKeys,
  GlobalDomAttributes,
  LinkDOMPropsKeys,
  PressEventsKeys,
} from '../../utils/types';

// 'elementType' is not needed because we expect the children
// of Link to dictate the element type.
export interface LinkProps
  extends Pick<
      AriaLinkOptions,
      | 'isDisabled'
      | PressEventsKeys
      | FocusablePropsKeys
      | LinkDOMPropsKeys
      | AriaLabelingPropsKeys
    >,
    SapphireStyleProps,
    GlobalDomAttributes,
    Pick<DOMAttributes, 'tabIndex'> {
  /**
   * The content of the Link can be a string, span or an anchor tag.
   */
  children: ReactNode;
  /**
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary';
}

export const Link: React.FC<LinkProps> = ({
  children,
  variant = 'primary',
  isDisabled = false,
  tabIndex,
  ...otherProps
}) => {
  useThemeCheck();

  const isAnchorElementType = typeof children !== 'string';

  const ref = useRef(null);
  const { linkProps, isPressed } = useLink(
    {
      ...otherProps,
      isDisabled,
      elementType: isAnchorElementType ? 'a' : 'span',
    },
    ref
  );
  const { styleProps } = useSapphireStyleProps(otherProps);

  return (
    <FocusRing focusRingClass={clsx(styles['is-focus'])}>
      {React.cloneElement(getWrappedElement(children), {
        /**
         * Since @react-aria/link@3.7.0, the returned props may contain undefined values
         * which will overwrite props passed to the wrapped <a>...</a> child.
         */
        ...filterUndefinedProps(linkProps),
        ref,
        // linkProps handles this for non-anchor elementTypes
        tabIndex:
          isAnchorElementType && isDisabled
            ? -1
            : tabIndex || linkProps.tabIndex,
        className: clsx(
          styleProps.className,
          styles['sapphire-link'],
          styles['js-focus'],
          {
            [styles['is-active']]: isPressed,
            [styles['is-disabled']]: isDisabled,
            [styles[`sapphire-link--secondary`]]: variant === 'secondary',
          }
        ),
        style: styleProps.style,
      })}
    </FocusRing>
  );
};

function filterUndefinedProps<T extends object>(
  props: T
): T /* Typing could be more accurate here, but it's not really needed*/ {
  const result = { ...props };
  for (const key in props) {
    if (result[key] === undefined) {
      delete result[key];
    }
  }
  return result;
}
